import React, { type ReactElement } from 'react';
import { type UniqueIdentifier } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, Checkbox, Icon, Typography } from '@saviynt/design-system';
import cx from 'classnames';

import { CF_NEU_STL, CF_SEC_BLD } from '../../../../shared/constants';
import { type WidgetMenuItem } from '../../../../shared/types';

import './ViolationsWidgetMenuItem.css';

export type ViolationsWidgetMenuItemOption = Omit<WidgetMenuItem, 'endpoint'>;

interface ViolationsWidgetMenuItemDraggableProps {
  option: ViolationsWidgetMenuItemOption;
  handleClick: (value: string, isChecked: boolean) => void;
}

/**
 * ViolationsWidgetMenuItem
 */
function ViolationsWidgetMenuItemDraggable({
  option,
  handleClick,
}: ViolationsWidgetMenuItemDraggableProps): ReactElement {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: option.value as UniqueIdentifier });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1,
    cursor: isDragging ? 'grab' : 'default',
  };
  const { label, value, description, icon, isChecked, isDisabled } = option;

  const itemClasses = cx(
    'Sod-ViolationsWidgetMenuItem-wrapper',
    isChecked && 'Sod-ViolationsWidgetMenuItem-wrapper--isChecked',
    isDisabled && 'Sod-ViolationsWidgetMenuItem-wrapper--isDisabled'
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={() => handleClick(value, isChecked)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleClick(value, isChecked);
        }
      }}
      // eslint-disable-next-line react/jsx-props-no-spreading -- Required to use DND attributes
      {...attributes}
      // eslint-disable-next-line react/jsx-props-no-spreading -- Required to use DND listeners
      {...listeners}
      role='button'
      tabIndex={0}>
      <Box tag='div' className={itemClasses}>
        <Box tag='div' className='Sod-ViolationsWidgetMenuItem-grabHandle'>
          <Icon kind='DragVertical' size='medium' />
        </Box>
        <Box tag='div' className='Sod-ViolationsWidgetMenuItem-contentWrapper'>
          <Box tag='div' className='Sod-ViolationsWidgetMenuItem-grabHandle'>
            <Icon kind={icon} size='medium' />{' '}
          </Box>
          <Box tag='div' className='Sod-ViolationsWidgetMenuItem-contentText'>
            <Typography kind='h3' color={CF_SEC_BLD}>
              {label}
            </Typography>
            {description && (
              <Typography kind='body2' color={CF_NEU_STL}>
                {description}
              </Typography>
            )}
          </Box>
          <Checkbox
            id={`violationsWidgetMenuItem-dragger-checkbox-${value}`}
            name={value}
            showHoverEffect={false}
            size='large'
            isChecked={isChecked}
            isDisabled={isDisabled}
            tabIndex={-1}
          />
        </Box>
      </Box>
    </div>
  );
}

interface ViolationsWidgetMenuItemPlaceholderProps {
  option: ViolationsWidgetMenuItemOption;
  handleClick: (value: string, isChecked: boolean) => void;
}

/**
 * ViolationsWidgetMenuItemPlaceholder
 */
function ViolationsWidgetMenuItemPlaceholder({
  option,
  handleClick,
}: ViolationsWidgetMenuItemPlaceholderProps): ReactElement {
  const { label, value, description, icon, isChecked, isDisabled } = option;

  const itemClasses = cx(
    'Sod-ViolationsWidgetMenuItem-wrapper',
    isChecked && 'Sod-ViolationsWidgetMenuItem-wrapper--isChecked',
    isDisabled && 'Sod-ViolationsWidgetMenuItem-wrapper--isDisabled'
  );

  return (
    <div
      role='button'
      onClick={() => {
        if (!isDisabled) {
          handleClick(value, isChecked);
        }
      }}
      onKeyDown={(event) => {
        if ((event.key === 'Enter' || event.key === ' ') && !isDisabled) {
          event.preventDefault();
          handleClick(value, isChecked);
        }
      }}
      tabIndex={isDisabled ? -1 : 0}>
      <Box tag='div' className={itemClasses}>
        <Box tag='div' className='Sod-ViolationsWidgetMenuItem-grabHandle' />
        <Box tag='div' className='Sod-ViolationsWidgetMenuItem-contentWrapper'>
          <Box tag='div' className='Sod-ViolationsWidgetMenuItem-grabHandle'>
            <Icon kind={icon} size='medium' />{' '}
          </Box>
          <Box tag='div' className='Sod-ViolationsWidgetMenuItem-contentText'>
            <Typography kind='h3' color={CF_SEC_BLD}>
              {label}
            </Typography>
            {description && (
              <Typography kind='body2' color={CF_NEU_STL}>
                {description}
              </Typography>
            )}
          </Box>
          <Checkbox
            id={`violationsWidgetMenuItem-dragger-checkbox-${value}`}
            name={value}
            showHoverEffect={false}
            size='large'
            isChecked={isChecked}
            isDisabled={isDisabled}
            tabIndex={-1}
          />
        </Box>
      </Box>
    </div>
  );
}

export {
  ViolationsWidgetMenuItemDraggable,
  ViolationsWidgetMenuItemPlaceholder,
};
